import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MeetingTemplateEditor,
  MeetingTemplateList,
  type MeetingTemplateDraft,
} from '@wundo/ui';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import {
  createMeetingTemplate,
  deleteMeetingTemplate,
  fetchMeetingTemplate,
  fetchMeetingTemplates,
  reorderMeetingTemplates,
  saveMeetingTemplate,
} from '@wundo/client/memberMeetingTemplates.ts';
import { fetchCohorts } from '@/lib/cohorts.ts';
import { fetchTracks } from '@/lib/tracks.ts';
import { meetingTemplateLabels } from '@/lib/meetingTemplateLabels.ts';
import { meetingTemplateRow } from '@/lib/meetingTemplateRow.ts';
import { orderTemplateSection } from '@/lib/orderTemplateSection.ts';
import type {
  MeetingTemplateItem,
  MeetingTemplateKind,
  MeetingTemplateScope,
} from '@wundo/shared/meetingTemplate.ts';
import { reorderWithinKind } from '@wundo/shared/reorderWithinKind.ts';
import { m } from '@/paraglide/messages.js';

export const Route = createFileRoute('/_app/settings/templates')({
  component: TemplateSettingsPage,
});

const MEETING_TEMPLATE_KINDS: MeetingTemplateKind[] = ['one_on_one', 'team', 'other'];

/* Which of the two screens is open. A new template starts as its block's kind. */
type Screen =
  | { kind: 'list' }
  | { kind: 'edit'; templateId: string }
  | { kind: 'create'; templateKind: MeetingTemplateKind };

/**
 * The gesprekstypes screen: a block per kind of meeting, the organisation's
 * types first, then your own. What a row offers comes from the server with
 * every row, never from a role read here: a member reads the organisation's,
 * an admin keeps both.
 */
export function TemplateSettingsPage() {
  const queryClient = useQueryClient();
  const [screen, setScreen] = useState<Screen>({ kind: 'list' });

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  const {
    data: templates,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['meetingTemplates', user?.id, 'manage'],
    queryFn: () => fetchMeetingTemplates(user!.id, 'manage'),
    enabled: Boolean(user?.id),
  });

  const editingId = screen.kind === 'edit' ? screen.templateId : null;
  const { data: editing } = useQuery({
    queryKey: ['meetingTemplate', user?.id, editingId],
    queryFn: () => fetchMeetingTemplate(user!.id, editingId!),
    enabled: Boolean(user?.id) && editingId !== null,
  });

  /*
   * Only whoever keeps the organisation's templates can aim one at a track or
   * cohort, and those endpoints are theirs alone.
   */
  const keepsOrganisation = (templates ?? []).some(
    (template) => template.scope === 'organisation' && template.editable,
  );
  const { data: cohorts = [] } = useQuery({
    queryKey: ['cohorts', user?.organisation_id],
    queryFn: () => fetchCohorts(user!.organisation_id),
    enabled: keepsOrganisation && user !== undefined,
  });
  const { data: tracks = [] } = useQuery({
    queryKey: ['tracks', user?.organisation_id],
    queryFn: () => fetchTracks(user!.organisation_id),
    enabled: keepsOrganisation && user !== undefined,
  });

  /*
   * Every write goes back to the list and re-reads it from the server: a
   * template's own row is what the agenda and the picker read next, so nothing
   * here is worth guessing at locally.
   */
  const write = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: async () => {
      setScreen({ kind: 'list' });
      await queryClient.invalidateQueries({ queryKey: ['meetingTemplates'] });
      await queryClient.invalidateQueries({ queryKey: ['meetingTemplate'] });
      await queryClient.invalidateQueries({ queryKey: ['meetings'] });
      await queryClient.invalidateQueries({ queryKey: ['meetingPage'] });
    },
  });

  /*
   * The new order shows before the request is answered: the row has already
   * moved under the pointer. A refused save puts the old order back, and the
   * refetch has the last word either way.
   */
  const reorder = useMutation({
    mutationFn: ({ scope, ids }: { scope: MeetingTemplateScope; ids: string[] }) =>
      reorderMeetingTemplates(user!.id, scope, ids),
    onMutate: async ({ scope, ids }) => {
      await queryClient.cancelQueries({ queryKey: ['meetingTemplates'] });
      const key = ['meetingTemplates', user?.id, 'manage'];
      const previous = queryClient.getQueryData<MeetingTemplateItem[]>(key);

      if (previous !== undefined) {
        queryClient.setQueryData<MeetingTemplateItem[]>(
          key,
          orderTemplateSection(previous, scope, ids),
        );
      }

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(
          ['meetingTemplates', user?.id, 'manage'],
          context.previous,
        );
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['meetingTemplates'] }),
  });

  if (isPending || !user) return null;
  if (isError) {
    return (
      <p className='text-base text-ink-muted'>{m.settings_templates_load_failed()}</p>
    );
  }

  const labels = meetingTemplateLabels();
  const saveFailed = write.isError ? m.settings_template_save_failed() : undefined;
  const backToList = () => {
    setScreen({ kind: 'list' });
    write.reset();
  };

  const save = ({ trackId, cohortId, kind, scope, ...draft }: MeetingTemplateDraft) => {
    const audience = { track_id: trackId, cohort_id: cohortId };
    if (screen.kind === 'create') {
      const templateKind = kind ?? screen.templateKind;
      write.mutate(() =>
        createMeetingTemplate(user.id, {
          ...draft,
          /* Only a keeper of the organisation's types is asked; anyone else writes their own. */
          scope: scope ?? 'personal',
          kind: templateKind,
          ...audience,
        }),
      );
      return;
    }
    if (screen.kind === 'edit') {
      const templateId = screen.templateId;
      write.mutate(() =>
        saveMeetingTemplate(user.id, templateId, { ...draft, ...audience }),
      );
    }
  };

  if (screen.kind === 'create') {
    return (
      <MeetingTemplateEditor
        template={null}
        /* Asked once, here: a type for two kinds of meeting is two types. */
        kind={screen.templateKind}
        kindEditable
        scope={keepsOrganisation ? 'organisation' : undefined}
        tracks={tracks}
        cohorts={cohorts}
        saving={write.isPending}
        error={saveFailed}
        onSave={save}
        onCancel={backToList}
        labels={labels}
      />
    );
  }

  if (screen.kind === 'edit') {
    /* The detail is read before it can be shown; until then the list stands. */
    if (!editing) return null;
    return (
      <MeetingTemplateEditor
        /* Remounts on another template, so the form starts from that one. */
        key={editing.id}
        template={{
          ...editing,
          trackId: editing.track?.id ?? null,
          cohortId: editing.cohort?.id ?? null,
        }}
        kind={editing.kind}
        /* A personal one has no audience, and a default stays everyone's. */
        tracks={editing.scope === 'organisation' ? tracks : []}
        cohorts={editing.scope === 'organisation' ? cohorts : []}
        wholeOrganisationOnly={editing.is_default}
        /* Whoever may change this template edits it, everybody else reads it. */
        readOnly={!editing.editable}
        canDelete
        /* Every kind always has a default to fall back to. */
        deleteRefusal={
          editing.is_default ? m.settings_template_delete_default_note() : undefined
        }
        saving={write.isPending}
        error={saveFailed}
        onSave={save}
        onDelete={async () => {
          /* A failure shows as the editor's error; the dialog just closes. */
          await write
            .mutateAsync(() => deleteMeetingTemplate(user.id, editing.id))
            .catch(() => undefined);
        }}
        onCancel={backToList}
        labels={labels}
      />
    );
  }

  const titles: Record<MeetingTemplateKind, string> = {
    one_on_one: m.meeting_kind_one_on_one(),
    team: m.meetings_kind_team(),
    other: m.settings_templates_other(),
  };

  /*
   * One run of a block: the rows of one scope and one kind. The server keeps
   * one order per scope, so a move inside a kind is sent as that whole scope.
   */
  const run = (meetingKind: MeetingTemplateKind, scope: MeetingTemplateScope) => {
    const scoped = templates.filter((template) => template.scope === scope);
    return {
      key: scope,
      templates: scoped
        .filter((template) => template.kind === meetingKind)
        .map(meetingTemplateRow),
      /* Your own types are yours to arrange, always. */
      canReorder: scope === 'personal' || keepsOrganisation,
      onReorder: (ids: string[]) =>
        reorder.mutate({ scope, ids: reorderWithinKind(scoped, meetingKind, ids) }),
    };
  };

  return (
    <MeetingTemplateList
      layout='blocks'
      /* The list went back to what the server holds; say why. */
      error={reorder.isError ? m.settings_templates_move_failed() : undefined}
      onOpen={(templateId) => setScreen({ kind: 'edit', templateId })}
      labels={labels}
      sections={MEETING_TEMPLATE_KINDS.map((meetingKind) => ({
        key: meetingKind,
        title: titles[meetingKind],
        emptyMessage: m.settings_templates_empty(),
        runs: [run(meetingKind, 'organisation'), run(meetingKind, 'personal')],
        canCreate: true,
        createLabel: m.settings_templates_new_label({ kind: titles[meetingKind] }),
        onCreate: () => setScreen({ kind: 'create', templateKind: meetingKind }),
      }))}
    />
  );
}
